/*
 * newsbot - forked from autobrr
 */

import {
  RssIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  QueueListIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { Link, Outlet } from "@tanstack/react-router";

import { useAuth } from "@contexts/AuthContext";

import { classNames } from "@utils";

interface NavTabType {
  name: string;
  href: string;
  icon: typeof Cog6ToothIcon;
  exact?: boolean;
  adminOnly?: boolean;
}

const subNavigation: NavTabType[] = [
  { name: "Feeds", href: "/settings/feeds", icon: RssIcon },
  { name: "Prompts", href: "/settings/prompts", icon: ChatBubbleLeftRightIcon },
  { name: "Users", href: "/settings/users", icon: UserGroupIcon, adminOnly: true },
  { name: "Account", href: "/settings/account", icon: UserCircleIcon },
  { name: "Batches", href: "/settings/batches", icon: QueueListIcon },
  { name: "Blocklist", href: "/settings/blocklist", icon: NoSymbolIcon },
];

interface NavLinkProps {
  item: NavTabType;
}

function SubNavLink({ item }: NavLinkProps) {
  return (
    <Link
      key={item.href}
      to={item.href}
      activeOptions={{ exact: item.exact }}
      search={{}}
      params={{}}
    >
      {({ isActive }) => (
        <span
          className={classNames(
            "transition group border-l-4 px-3 py-2 flex items-center text-sm font-medium",
            isActive
              ? "font-bold bg-blue-100 dark:bg-gray-700 border-sky-500 dark:border-blue-500 text-sky-700 dark:text-gray-200 hover:bg-blue-200 dark:hover:bg-gray-600 hover:text-sky-900 dark:hover:text-white"
              : "border-transparent text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
          )}
        >
          <item.icon
            className="text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 shrink-0 -ml-1 mr-3 h-6 w-6"
            aria-hidden="true"
          />
          <span className="truncate">{item.name}</span>
        </span>
      )}
    </Link>
  );
}

interface MobileTabLinkProps {
  item: NavTabType;
}

function MobileTabLink({ item }: MobileTabLinkProps) {
  return (
    <Link
      key={item.href}
      to={item.href}
      activeOptions={{ exact: item.exact }}
      search={{}}
      params={{}}
    >
      {({ isActive }) => (
        <span
          className={classNames(
            "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors",
            isActive
              ? "border-sky-500 dark:border-blue-500 text-sky-700 dark:text-blue-400"
              : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
          )}
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {item.name}
        </span>
      )}
    </Link>
  );
}

interface SidebarNavProps {
  subNavigation: NavTabType[];
  isAdmin: boolean;
}

function SidebarNav({ subNavigation, isAdmin }: SidebarNavProps) {
  const filteredNav = subNavigation.filter(item => !item.adminOnly || isAdmin);

  return (
    <>
      {/* Mobile: horizontal scrollable tab bar */}
      <nav
        className="lg:hidden relative border-b border-gray-200 dark:border-gray-700 mb-6"
        aria-label="Settings"
      >
        <div className="overflow-x-auto">
          <div className="flex space-x-1 px-4 min-w-max">
            {filteredNav.map((item, idx) => (
              <MobileTabLink key={item.href + idx} item={item} />
            ))}
          </div>
        </div>
        {/* Fade indicator for scroll */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-800" />
      </nav>

      {/* Desktop: vertical sidebar */}
      <aside className="hidden lg:block lg:col-span-3 py-2 border-r border-gray-150 dark:border-gray-725">
        <nav className="space-y-1" aria-label="Settings">
          {filteredNav.map((item, idx) => (
            <SubNavLink key={item.href + idx} item={item} />
          ))}
        </nav>
      </aside>
    </>
  );
}

export function Settings() {
  const { isAdmin } = useAuth();

  return (
    <main>
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-black dark:text-white">Settings</h1>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-6 px-2 sm:px-6 lg:pb-16 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-table border border-gray-250 dark:border-gray-775">
          <div className="lg:grid lg:grid-cols-12">
            <SidebarNav subNavigation={subNavigation} isAdmin={isAdmin} />
            <Outlet />
          </div>
        </div>
      </div>
    </main>
  );
}
